import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';
import { LOGO_SMALL } from './constants';
import { useLang } from '../../contexts/LanguageContext';
import { WALKTHROUGH_MAILTO } from '../../i18n/trust';
import { ThemeToggle } from '@/components/ThemeToggle';
import { InstallAppChip } from '../InstallAppChip';

// Small inline NL/EN toggle. Lives next to the primary CTA on desktop
// and at the top of the mobile menu so it is reachable before users
// scroll the long landing page.
const LangSwitch = ({ className = '' }) => {
  const { lang, setLang } = useLang();
  const base = 'px-2.5 py-1 text-xs font-semibold rounded-md transition-colors';
  const active = 'bg-[#00CED1] text-[#0a0a0a]';
  const idle = 'text-white/55 hover:text-white';
  return (
    <div
      className={`inline-flex items-center gap-1 p-1 rounded-lg border border-white/10 bg-white/[0.03] ${className}`}
      role="group"
      aria-label="Language selector"
    >
      <button
        type="button"
        className={`${base} ${lang === 'nl' ? active : idle}`}
        onClick={() => setLang('nl')}
        data-testid="lang-toggle-nl"
        aria-pressed={lang === 'nl'}
      >
        NL
      </button>
      <button
        type="button"
        className={`${base} ${lang === 'en' ? active : idle}`}
        onClick={() => setLang('en')}
        data-testid="lang-toggle-en"
        aria-pressed={lang === 'en'}
      >
        EN
      </button>
    </div>
  );
};

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useLang('navbar');

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <span className="relative inline-flex items-start">
              <img src="/brand/kanriva-logo.png" alt="Kanriva™" className="h-8 sm:h-9 w-auto" />
            </span>
          </Link>
          <div className="hidden md:flex flex-1 items-center justify-center gap-4 lg:gap-6 min-w-0">
            <a href="#platform" className="text-sm font-medium text-white/50 hover:text-[#00CED1] transition-colors whitespace-nowrap">{t.platform}</a>
            <a href="#flowcrew" className="text-sm font-medium text-white/50 hover:text-[#00CED1] transition-colors whitespace-nowrap">{t.flowcrew}</a>
            <a href="#pricing" className="hidden xl:inline text-sm font-medium text-white/50 hover:text-[#00CED1] transition-colors whitespace-nowrap">{t.modules}</a>
            <Link to="/security" className="text-sm font-medium text-white/50 hover:text-[#00CED1] transition-colors whitespace-nowrap" data-testid="nav-security-link">{t.security}</Link>
          </div>
          <div className="hidden md:flex items-center gap-3 shrink-0">
            <div className="hidden xl:block"><InstallAppChip /></div>
            <ThemeToggle className="mr-1" />
            <LangSwitch />
            <Button className="bg-[#00CED1] hover:bg-[#00b4b7] text-[#0a0a0a] font-semibold" asChild data-testid="nav-login-btn">
              <Link to="/login">{t.loginShort}</Link>
            </Button>
            <Button variant="outline" className="hidden lg:inline-flex border-[#00CED1]/40 bg-transparent text-[#00CED1] hover:bg-[#00CED1]/10" asChild data-testid="nav-walkthrough-btn">
              <a href={WALKTHROUGH_MAILTO}>{t.walkthroughShort}</a>
            </Button>
            <Button variant="ghost" className="hidden xl:inline-flex text-white/60 hover:text-white px-3" asChild data-testid="nav-peek-btn">
              <Link to="/demo">{t.peekButton}</Link>
            </Button>
            <Button variant="ghost" className="hidden 2xl:inline-flex text-white/60 hover:text-white px-3" asChild data-testid="get-started-btn">
              <Link to="/signup">{t.trialButton}</Link>
            </Button>
          </div>
          <button className="md:hidden p-2 text-white/50 hover:text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#0a0a0a] border-t border-white/10 px-4 py-4 space-y-4">
          <div className="flex items-center justify-center"><InstallAppChip /></div>
          <LangSwitch className="w-full justify-center" />
          <a href="#platform" className="block text-sm font-medium text-white/80">{t.platform}</a>
          <a href="#flowcrew" className="block text-sm font-medium text-white/80">{t.flowcrew}</a>
          <a href="#benefits" className="block text-sm font-medium text-white/80">{t.howWeWork}</a>
          <a href="#pricing" className="block text-sm font-medium text-white/80">{t.modules}</a>
          <a href="#creator" className="block text-sm font-medium text-white/80">{t.madeBy}</a>
          <a href="#contact" className="block text-sm font-medium text-white/80">{t.contact}</a>
          <Link to="/security" className="block text-sm font-medium text-[#00CED1]" data-testid="nav-security-link-mobile">{t.security}</Link>
          <Button className="w-full bg-[#00CED1] hover:bg-[#00b4b7] text-[#0a0a0a] font-semibold" asChild data-testid="nav-login-btn-mobile">
            <Link to="/login">{t.loginButton}</Link>
          </Button>
          <Button variant="outline" className="w-full border-[#00CED1]/40 bg-transparent text-[#00CED1]" asChild data-testid="nav-walkthrough-btn-mobile">
            <a href={WALKTHROUGH_MAILTO}>{t.walkthroughShort}</a>
          </Button>
          <Button variant="outline" className="w-full border-white/15 bg-transparent text-white/80" asChild data-testid="nav-peek-btn-mobile">
            <Link to="/demo">{t.peekButton}</Link>
          </Button>
          <Button variant="ghost" className="w-full text-white/70" asChild>
            <Link to="/signup">{t.trialButton}</Link>
          </Button>
          <p className="pt-1 text-center text-[11px] leading-relaxed text-white/45" data-testid="nav-language-note-mobile">
            {t.languageNote}
          </p>
        </div>
      )}
    </nav>
  );
};
